import React, { useState } from 'react';
import { Card, Form, Input, Button, message, Space, Divider, Alert } from 'antd';
import {
  applyCoupon,
  bindShop,
  unbindShop,
  getUserInfo,
  getStoreList,
  userLogin,
  userRegister,
  requestTemporaryPassword,
  changeUserPassword,
} from '../services/api';

const UserTestPage = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiLogs] = useState([]);

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const res = await userLogin(values);
      setUserInfo(res);
      if (res?.mustChangePassword) {
        message.warning('当前登录的是临时密码，请立即修改密码。');
      } else {
        message.success('用户登录成功');
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values) => {
    setLoading(true);
    try {
      await userRegister(values);
      message.success('注册成功');
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestTemporaryPassword = async (values) => {
    setLoading(true);
    try {
      const res = await requestTemporaryPassword(values.email);
      message.success(res?.message || '临时密码申请成功，请检查邮箱。');
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (values) => {
    setLoading(true);
    try {
      await changeUserPassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      setUserInfo((prev) => ({
        ...(prev || {}),
        mustChangePassword: false,
      }));
      message.success('密码修改成功');
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUseCoupon = async (code) => {
    setLoading(true);
    try {
      const res = await applyCoupon(code);
      const data = res?.data || res;
      message.success(`激活码使用成功，有效期: ${data?.expiryDays ?? '-'} 天`);
      const userRes = await getUserInfo();
      setUserInfo(userRes?.data || userRes);
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBindShop = async (shopId) => {
    setLoading(true);
    try {
      await bindShop(shopId);
      message.success('店铺绑定成功');
      const res = await getUserInfo();
      setUserInfo(res?.data || res);
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnbindShop = async (shopId) => {
    setLoading(true);
    try {
      await unbindShop(shopId);
      message.success('店铺解绑成功');
      const res = await getUserInfo();
      setUserInfo(res?.data || res);
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGetUserInfo = async () => {
    setLoading(true);
    try {
      const res = await getUserInfo();
      setUserInfo(res?.data || res);
      message.success('获取用户信息成功');
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGetShops = async () => {
    setLoading(true);
    try {
      const res = await getStoreList();
      const shops = res?.shops || res?.data?.shops || [];
      message.success('获取店铺列表成功');
      setUserInfo((prev) => ({
        ...(prev || {}),
        shops,
      }));
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Card title="用户登录" loading={loading}>
          <Form onFinish={handleLogin} layout="vertical">
            <Form.Item name="username" label="用户名" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="password" label="密码" rules={[{ required: true }]}>
              <Input.Password />
            </Form.Item>
            <Button type="primary" htmlType="submit">
              登录
            </Button>
          </Form>
        </Card>

        <Card title="临时密码找回" loading={loading}>
          <Form onFinish={handleRequestTemporaryPassword} layout="vertical">
            <Form.Item name="email" label="邮箱" rules={[{ required: true, type: 'email' }]}>
              <Input placeholder="输入邮箱后发送临时密码" />
            </Form.Item>
            <Button type="primary" htmlType="submit">
              发送临时密码
            </Button>
          </Form>
        </Card>

        <Card title="登录后修改密码" loading={loading}>
          {userInfo?.mustChangePassword && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="当前账号使用的是临时密码，请先修改为正式密码。"
            />
          )}
          <Form onFinish={handleChangePassword} layout="vertical">
            <Form.Item name="currentPassword" label="当前密码" rules={[{ required: true }]}>
              <Input.Password placeholder="输入当前密码或临时密码" />
            </Form.Item>
            <Form.Item
              name="newPassword"
              label="新密码"
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 6, message: '新密码至少 6 位' },
              ]}
            >
              <Input.Password placeholder="输入新密码" />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label="确认新密码"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: '请再次输入新密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的新密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="再次输入新密码" />
            </Form.Item>
            <Button type="primary" htmlType="submit">
              修改密码
            </Button>
          </Form>
        </Card>

        <Card title="用户注册" loading={loading}>
          <Form onFinish={handleRegister} layout="vertical">
            <Form.Item name="username" label="用户名" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="password" label="密码" rules={[{ required: true }]}>
              <Input.Password />
            </Form.Item>
            <Form.Item name="email" label="邮箱" rules={[{ required: true, type: 'email' }]}>
              <Input />
            </Form.Item>
            <Button type="primary" htmlType="submit">
              注册
            </Button>
          </Form>
        </Card>

        <Card title="使用激活码" loading={loading}>
          <Space>
            <Input.Search placeholder="输入激活码" onSearch={handleUseCoupon} enterButton="使用" />
          </Space>
        </Card>

        <Card title="店铺管理" loading={loading}>
          <Space>
            <Input.Search placeholder="输入店铺ID" onSearch={handleBindShop} enterButton="绑定" />
            <Input.Search placeholder="输入店铺ID" onSearch={handleUnbindShop} enterButton="解绑" />
          </Space>
        </Card>

        <Card title="用户信息" loading={loading}>
          <Space>
            <Button onClick={handleGetUserInfo}>获取用户信息</Button>
            <Button onClick={handleGetShops}>获取店铺列表</Button>
          </Space>
          <Divider />
          <pre>{JSON.stringify(userInfo, null, 2)}</pre>

          {userInfo?.shops && (
            <div style={{ marginTop: 16 }}>
              <h4>店铺列表</h4>
              <ul>
                {userInfo.shops.map((shop, index) => (
                  <li key={index}>
                    {shop.shopId} (绑定时间: {new Date(shop.bindDate).toLocaleString()})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>

        <Card title="接口日志" style={{ marginTop: 16 }}>
          <div
            style={{
              height: 200,
              overflowY: 'auto',
              backgroundColor: '#f5f5f5',
              padding: 8,
              borderRadius: 4,
            }}
          >
            {apiLogs.map((log, index) => (
              <div
                key={index}
                style={{
                  marginBottom: 4,
                  color: log.type === 'error' ? 'red' : 'inherit',
                }}
              >
                [{log.timestamp}] {log.message}
              </div>
            ))}
          </div>
        </Card>
      </Space>
    </div>
  );
};

export default UserTestPage;
